import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Breadcrumbs,
  Link,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { api } from '../../../services/authService';

const AddLeadsToList = () => {
  const navigate = useNavigate();
  const { listId } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  
  // State management
  const [list, setList] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStep, setFilterStep] = useState('');
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [funnelSteps, setFunnelSteps] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLeads, setTotalLeads] = useState(0);

  useEffect(() => {
    fetchListData();
  }, [listId]);

  useEffect(() => {
    if (list) {
      fetchAvailableLeads();
    }
  }, [list, page, rowsPerPage, searchTerm, filterStatus, filterStep]);

  const fetchListData = async () => {
    setLoading(true);
    try {
      // First fetch the list data
      const listResponse = await api.get(`/crm/lists/${listId}/`);
      setList(listResponse.data);
      
      // Initialize arrays to prevent undefined errors
      setLeadStatuses([]);
      setFunnelSteps([]);
      
      // Then fetch campaign-dependent data if campaign exists
      if (listResponse.data.campaign) {
        try {
          const [statusesResponse, stepsResponse] = await Promise.all([
            api.get(`/crm/lead-statuses/`),
            api.get(`/crm/steps/?campaign_id=${listResponse.data.campaign}`)
          ]);
          
          setLeadStatuses(statusesResponse.data || []);
          setFunnelSteps(stepsResponse.data || []);
        } catch (apiError) {
          console.error('Error fetching campaign-dependent data:', apiError);
          // Keep empty arrays if API calls fail
        }
      } else {
        // If no campaign, fetch all statuses and steps (or set empty arrays)
        try {
          const [statusesResponse, stepsResponse] = await Promise.all([
            api.get('/crm/lead-statuses/'),
            api.get('/crm/steps/')
          ]);
          
          setLeadStatuses(statusesResponse.data || []);
          setFunnelSteps(stepsResponse.data || []);
        } catch (apiError) {
          console.error('Error fetching all statuses and steps:', apiError);
          // Keep empty arrays if API calls fail
        }
      }
    } catch (error) {
      console.error('Error fetching list data:', error);
      // Initialize arrays to prevent undefined errors
      setLeadStatuses([]);
      setFunnelSteps([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        page_size: rowsPerPage,
        exclude_list: listId,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterStep && { step: filterStep }),
        ...(list?.account && { account_id: list.account }),
        ...(list?.campaign && { campaign_id: list.campaign }),
        ...(list?.funnel && { funnel_id: list.funnel })
      });
      
      const response = await api.get(`/crm/leads/?${params.toString()}`);
      setLeads(response.data.results || []);
      setTotalLeads(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
      setTotalLeads(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(leads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleAddLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    setSaving(true);
    try {
      await api.post(`/crm/lists/${listId}/add_leads/`, {
        lead_ids: selectedLeads
      });
      navigate(`/crm/lists/${listId}`);
    } catch (error) {
      console.error('Error adding leads:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !list) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">List not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="/crm/dashboard">
            Dashboard
          </Link>
          <Link color="inherit" href="/crm/lists">
            Lead Lists
          </Link>
          <Link color="inherit" href={`/crm/lists/${listId}`}>
            {list.name}
          </Link>
          <Typography color="text.primary">Add Leads</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" color="text.primary">
            Add Leads to {list.name}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate(`/crm/lists/${listId}`)}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddLeads}
              disabled={saving || selectedLeads.length === 0}
            >
              Add {selectedLeads.length} Leads
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {leadStatuses.map(status => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Step</InputLabel>
                <Select
                  value={filterStep}
                  onChange={(e) => setFilterStep(e.target.value)}
                  label="Step"
                >
                  <MenuItem value="">All Steps</MenuItem>
                  {funnelSteps.map(step => (
                    <MenuItem key={step.id} value={step.id}>
                      {step.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary">
                {totalLeads} leads available
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Selected Leads Summary */}
      {selectedLeads.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedLeads.length} leads selected for addition to the list
        </Alert>
      )}

      {/* Leads Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Leads
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={leads.length > 0 && selectedLeads.length === leads.length}
                      indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No leads available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {lead.first_name} {lead.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.phone_number}</TableCell>
                      <TableCell>
                        <Chip 
                          label={lead.status?.name || 'No Status'} 
                          size="small"
                          color={lead.status?.name === 'Active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={lead.current_step?.name || 'No Step'} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(lead.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalLeads)} of {totalLeads} leads
            </Typography>
            <Box>
              <Button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                sx={{ mr: 1 }}
              >
                Previous
              </Button>
              <Button
                disabled={(page + 1) * rowsPerPage >= totalLeads}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddLeadsToList; 